import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../../../components/layout/PublicLayout';
import { SEOMetaTags } from '../../../components/shared/SEOMetaTags';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateFormatter';
import { useToast } from '../../../hooks/useToast';
import ReactConfetti from 'react-confetti';

const TicketDetails: React.FC = () => {
  const { ticketCode } = useParams<{ ticketCode: string }>();
  const [ticket, setTicket] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [previousStatus, setPreviousStatus] = useState<string | null>(null);
  const [windowDimensions, setWindowDimensions] = useState<{ width: number; height: number }>({ 
    width: window.innerWidth, 
    height: window.innerHeight 
  });
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Fonction pour récupérer les détails du ticket
  const fetchTicket = useCallback(async () => {
    try {
      if (!ticketCode) {
        setError('Code de ticket non valide');
        return;
      }

      // Étape 1: Récupérer le ticket par son QR code
      const { data: attendee, error: attendeeError } = await supabase
        .from('event_attendees')
        .select('*')
        .eq('qr_code', ticketCode)
        .single();

      if (attendeeError) {
        console.error('Erreur lors de la récupération du ticket:', attendeeError);
        throw new Error(attendeeError.message);
      }

      if (!attendee) {
        setError('Ticket non trouvé');
        return;
      }

      // Étape 2: Récupérer les informations de l'événement
      let eventData = null;
      if (attendee.event_id) {
        const { data: event } = await supabase
          .from('events')
          .select('*')
          .eq('id', attendee.event_id)
          .single();
        
        eventData = event;
      }

      // Étape 3: Récupérer les informations de la commande
      let orderData = null;
      let ticketData = null;
      if (attendee.ticket_order_id) {
        const { data: order } = await supabase
          .from('ticket_orders')
          .select('*')
          .eq('id', attendee.ticket_order_id)
          .single();
        
        orderData = order;

        // Étape 4: Récupérer les informations du ticket
        if (orderData && orderData.ticket_id) {
          const { data: ticket } = await supabase
            .from('tickets')
            .select('*')
            .eq('id', orderData.ticket_id)
            .single();
          
          ticketData = ticket;
        }
      }

      // Combiner toutes les données
      const completeTicketData = {
        ...attendee,
        event: eventData,
        ticket_order: orderData ? {
          ...orderData,
          ticket: ticketData
        } : null
      };

      // Vérifier si le statut a changé de 'pending' à 'checked_in'
      if (previousStatus === 'pending' && completeTicketData.status === 'checked_in') {
        // Déclencher l'effet de confetti
        setShowConfetti(true);
        showToast('Votre ticket a été validé avec succès!', 'success');
        
        // Arrêter l'effet après 8 secondes
        setTimeout(() => {
          setShowConfetti(false);
        }, 8000);
      }

      // Mettre à jour le statut précédent
      setPreviousStatus(completeTicketData.status);
      setTicket(completeTicketData);
    } catch (err: any) {
      console.error('Erreur lors de la récupération du ticket:', err);
      setError(err.message || 'Erreur lors de la récupération du ticket');
      showToast('Erreur lors de la récupération du ticket', 'error');
    } finally {
      setLoading(false);
    }
  }, [ticketCode, previousStatus, showToast]);

  // Effet initial pour charger le ticket
  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchTicket();
  }, [fetchTicket]);

  // Effet pour actualiser le statut du ticket en temps réel
  useEffect(() => {
    // Mettre à jour les dimensions de la fenêtre pour le confetti
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);

    // Abonnement aux changements en temps réel sur la table event_attendees
    const channel = supabase.channel(`ticket-status-${ticketCode}`);
    
    const subscription = channel
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'event_attendees',
        filter: `qr_code=eq.${ticketCode}`,
      }, (payload) => {
        console.log('Changement détecté:', payload);
        // Actualiser les données du ticket lorsqu'il y a un changement
        fetchTicket();
      })
      .subscribe((status) => {
        console.log('Statut de l\'abonnement:', status);
      });

    // Actualiser le statut toutes les 5 secondes
    const intervalId = setInterval(() => {
      if (!loading) {
        fetchTicket();
      }
    }, 5000);

    return () => {
      window.removeEventListener('resize', handleResize);
      // Nettoyer proprement l'abonnement Supabase
      channel.unsubscribe();
      clearInterval(intervalId);
    };
  }, [fetchTicket, loading, ticketCode]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Ticket pour ${ticket?.event?.title || 'Événement'}`,
          text: `Voici mon ticket pour ${ticket?.event?.title || 'l\'événement'}`,
          url: window.location.href
        });
      } else {
        // Fallback pour les navigateurs qui ne supportent pas l'API Web Share
        navigator.clipboard.writeText(window.location.href);
        showToast('Lien copié dans le presse-papier', 'success');
      }
    } catch (error) {
      console.error('Erreur lors du partage:', error);
      showToast('Erreur lors du partage du ticket', 'error');
    }
  };

  return (
    <PublicLayout>
      {/* Effet de confetti lorsque le ticket est validé */}
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.15}
          colors={['#FFD700', '#FFA500', '#FF4500', '#FF6347', '#FF8C00', '#FFFF00']}
          confettiSource={{
            x: windowDimensions.width / 2,
            y: windowDimensions.height / 3,
            w: 0,
            h: 0
          }}
        />
      )}
      
      {/* Background avec dégradé et motif */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-black opacity-90"></div>
        <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-5 bg-cover"></div>
      </div>
      <SEOMetaTags
        title={ticket ? `Ticket - ${ticket.event?.title || 'Événement'}` : 'Détails du ticket'}
        description="Détails de votre ticket d'événement"
      />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <button 
            onClick={() => navigate(-1)} 
            className="mb-6 flex items-center text-yellow-500 hover:text-yellow-400 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Retour
          </button>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <h3 className="text-xl font-medium text-red-400 mb-2">Erreur</h3>
              <p className="text-gray-300">{error}</p>
              <button 
                onClick={() => navigate('/tickets/my-tickets')} 
                className="mt-4 bg-red-500/20 hover:bg-red-500/30 text-red-300 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Retourner à mes tickets
              </button>
            </div>
          ) : ticket ? (
            <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
              {/* En-tête du ticket avec titre de l'événement et statut */}
              <div className="p-6 bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-b border-gray-700/50">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2">
                  <div className="mb-4 md:mb-0">
                    <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-yellow-300 to-yellow-500 bg-clip-text text-transparent">
                      {ticket.event?.title || 'Événement sans titre'}
                    </h2>
                    {ticket.event?.subtitle && (
                      <p className="text-gray-300 text-sm mt-1">{ticket.event.subtitle}</p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${
                      ticket.status === 'checked_in' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 
                      ticket.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' : 
                      'bg-red-500/20 text-red-300 border border-red-500/30'
                    }`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        ticket.status === 'checked_in' ? 'bg-green-400' : 
                        ticket.status === 'pending' ? 'bg-yellow-400' : 
                        'bg-red-400'
                      }`}></span>
                      {ticket.status === 'checked_in' ? 'Utilisé' : 
                       ticket.status === 'pending' ? 'Non utilisé' : 
                       'Annulé'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* QR Code du ticket - Affiché en grand pour faciliter le scan */}
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border-2 border-yellow-500/30 rounded-lg p-8 text-center shadow-lg shadow-yellow-500/5 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/5 to-transparent opacity-50 z-0"></div>
                    <div className="relative z-10 w-full flex flex-col items-center">
                      <div className="bg-white p-5 rounded-lg shadow-md mx-auto max-w-xs">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(ticket.qr_code)}`}
                          alt="QR Code"
                          className="w-full h-auto max-w-[300px] mx-auto"
                        />
                      </div>
                      <p className="text-sm text-yellow-300 font-mono bg-black/30 py-2 px-3 rounded break-all mt-6 max-w-full">{ticket.qr_code}</p>
                    </div>
                  </div>
                </div>

                {/* Détails du ticket */}
                <div className="bg-white/5 p-6 rounded-lg mb-6">
                  <p className="text-lg text-gray-300 mb-4 font-medium">Détails du ticket :</p>
                  <ul className="text-sm text-gray-400 space-y-3">
                    <li className="flex">
                      <span className="text-gray-300 w-28 flex-shrink-0">Nom :</span>
                      <span className="text-gray-200">{ticket.attendee_name || 'Non spécifié'}</span>
                    </li>
                    <li className="flex">
                      <span className="text-gray-300 w-28 flex-shrink-0">Type :</span>
                      <span className="text-gray-200">
                        {ticket.ticket_order?.ticket?.ticket_type === 'standard' ? 'Standard' :
                        ticket.ticket_order?.ticket?.ticket_type === 'vip' ? 'VIP' :
                        ticket.ticket_order?.ticket?.ticket_type === 'free' ? 'Gratuit' :
                        ticket.ticket_order?.ticket?.ticket_type === 'early_bird' ? 'Early Bird' : 'Standard'}
                      </span>
                    </li>
                    <li className="flex">
                      <span className="text-gray-300 w-28 flex-shrink-0">Prix :</span>
                      <span className="text-gray-200">
                        {ticket.ticket_order?.ticket?.price ? 
                          `${ticket.ticket_order.ticket.price} ${ticket.ticket_order.ticket.currency || 'FCFA'}` : 'Gratuit'}
                      </span>
                    </li>
                    <li className="flex">
                      <span className="text-gray-300 w-28 flex-shrink-0">Date :</span>
                      <span className="text-gray-200">
                        {ticket.event?.start_date ? formatDate(ticket.event.start_date.toString()) : 'Date non spécifiée'}
                      </span>
                    </li>
                    <li className="flex">
                      <span className="text-gray-300 w-28 flex-shrink-0">Lieu :</span>
                      <span className="text-gray-200">{ticket.event?.location || 'Lieu non spécifié'}</span>
                    </li>
                    <li className="flex">
                      <span className="text-gray-300 w-28 flex-shrink-0">Commande :</span>
                      <span className="text-gray-200">{ticket.ticket_order?.order_code || 'N/A'}</span>
                    </li>
                    <li className="flex">
                      <span className="text-gray-300 w-28 flex-shrink-0">Email :</span>
                      <span className="text-gray-200">{ticket.attendee_email || 'Non spécifié'}</span>
                    </li>
                    {ticket.attendee_phone && (
                      <li className="flex">
                        <span className="text-gray-300 w-28 flex-shrink-0">Téléphone :</span>
                        <span className="text-gray-200">{ticket.attendee_phone}</span>
                      </li>
                    )}
                  </ul>
                </div>

                {/* Instructions */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-medium text-yellow-400 mb-2">Instructions</h3>
                  <ul className="text-sm text-gray-300 list-disc pl-5 space-y-2">
                    <li>Présentez ce <strong>code QR</strong> à l'entrée de l'événement</li>
                    <li>Vous pouvez <strong>imprimer</strong> ce ticket ou le présenter sur votre <strong>téléphone</strong></li>
                    <li>Ce ticket ne peut être utilisé qu'<strong>une seule fois</strong></li>
                    <li>Assurez-vous que l'écran de votre téléphone est suffisamment lumineux pour que le code QR soit lisible</li>
                  </ul>
                </div>

                {/* Boutons d'action */}
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <button 
                    onClick={handleShare} 
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-4 px-6 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                    </svg>
                    Partager ce ticket
                  </button>
                  <button 
                    onClick={() => window.print()} 
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-4 px-6 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                    </svg>
                    Imprimer
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </PublicLayout>
  );
};

export default TicketDetails;
